import React from 'react';
import { CONFIG } from '@hero/core';
import { ItemType } from '@hero/schema';
import { formatVehicleText } from '@hero/web-utils';
import { IconProps, ItemIconDynamic } from '@hero/web-icons';
import { SelectAuto, SelectAutoOption } from '@hero/web-components';
import { useTranslation } from 'react-i18next';

interface OrderItemTypeSelectProps {
  itemType: ItemType | undefined;
  onMaterialChange: (value: ItemType) => void;
  /** Optional placeholder text */
  placeholder?: string;
  /** Optional className */
  className?: string;
  /** Optional icon props */
  iconProps?: IconProps;
  /** Optional disabled state */
  disabled?: boolean;
}

/**
 * OrderMaterialTypeSelect - A component for selecting material/item type.
 * Includes the select field with material icons and labels.
 *
 * @example
 * ```tsx
 * <OrderMaterialTypeSelect
 *   material={selectedMaterial}
 *   onMaterialChange={(material) => setSelectedMaterial(material)}
 *   placeholder="Select material type"
 * />
 * ```
 */
export function OrderItemTypeSelect({
  itemType,
  onMaterialChange,
  placeholder,
  className,
  iconProps,
  disabled = false,
}: OrderItemTypeSelectProps) {
  const { t } = useTranslation(['m-order']);

  const materialOptions = CONFIG.order.ordersLoadConfig.itemTypeOptions;

  // `formatVehicleText` is the fallback, not the label: it only prettifies the
  // enum in English, so it reads as untranslated copy in Arabic. It stays as
  // `defaultValue` so a newly added `ItemType` renders readably instead of
  // leaking its translation key while the copy catches up.
  const options: SelectAutoOption[] = materialOptions.map(item => ({
    value: item,
    label: t(`m-order:item_types.${item}`, formatVehicleText(item)),
    icon: <ItemIconDynamic itemType={item} size={'medium'} variant={'primary'} {...iconProps} />,
  }));

  return (
    <SelectAuto
      options={options}
      value={itemType}
      onChange={val => onMaterialChange(val as ItemType)}
      placeholder={placeholder || t('m-order:load_selects.item_type_placeholder')}
      disabled={disabled}
      className={className}
      triggerClassName="text-[16px] font-[600] leading-[24px]"
    />
  );
}
